import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject, shareReplay, switchMap, tap } from 'rxjs';
import { MenuItem } from './classes/menu-item';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { debug } from '../shared/rxjs/debug';
import { filter, map } from 'rxjs/operators';
import { LimitedStack } from '../shared/classes/LimitedStack';
import { AuthService } from '../auth/auth.service';
import { HelperService } from '../shared/helper.service';
import { AppMenu } from './classes/app-menu';
import { EventsService } from '../realguest/services/events.service';
import { AccountCreditsData } from './classes/account-credits-data';
import { notNullOrUndefined } from '../shared/rxjs/notNullOrUndefined';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CoreService {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly hs = inject(HelperService);
  private readonly es = inject(EventsService);
  private readonly http = inject(HttpClient);
  private readonly dialog = inject(MatDialog);


  menuItems$: Observable<AppMenu>;
  userMenu$: Observable<MenuItem[]>;
  currentRoute$ = new ReplaySubject<string>(1);
  userMenuExpanded$ = new BehaviorSubject<boolean>(false);
  collapseMenu$ = new BehaviorSubject<boolean>(false);
  /** Sidebar admin section expand/collapse — collapsed by default to de-emphasise it. */
  adminExpanded$ = new BehaviorSubject<boolean>(false);

  navHistory = new LimitedStack<string>(50);

  accountCreditsData$: Observable<AccountCreditsData>;

  constructor() {
    this.router.events.pipe(
      tap((event) => {
        if (event instanceof NavigationStart) {
          if (this.navHistory.peek() !== event.url) {
            this.hs.toolbarComponent$.next(null);
            this.hs.showBackBtn$.next(false);
          }
        }
      }),
      filter((event) => event instanceof NavigationEnd),
      map((event) => (event as NavigationEnd).urlAfterRedirects),
      tap((route) => {
        if (this.auth.user$.value && route.startsWith('/auth')) {
          this.router.navigate(['/']);
          return;
        }
        if (this.es.selectedEvent$.value) {
          if (!route.startsWith('/events/')) {
            this.es.selectedEvent$.next(null);
          }
        }

        this.navHistory.push(route);
        this.currentRoute$.next(route);
      })
    ).subscribe();

    this.userMenu$ = this.auth.user$.pipe(
      switchMap((user) => combineLatest([
        this.currentRoute$
      ]).pipe(
        map(([route]) => {
          if (user) {
            const userMenu = [
              // new MenuItem({
              //   label: 'Impostazioni',
              //   url: '/settings',
              //   icon: 'settings',
              //   imageType: 'icon',
              //   isActive: false,
              //   visible: true,
              // }, route),
              new MenuItem({
                label: 'menu.profile',
                url: `/profile`,
                imageType: 'icon',
                icon: 'account_circle',
                isActive: false,
                visible: true
              }, route),
              new MenuItem({
                label: 'menu.payments',
                url: `/payments`,
                imageType: 'icon',
                icon: 'paid',
                isActive: false,
                visible: true
              }, route),
              new MenuItem({
                label: 'menu.help',
                url: `/help`,
                imageType: 'icon',
                icon: 'help',
                isActive: false,
                visible: true
              }, route),
              new MenuItem({
                label: 'menu.logout',
                url: '',
                icon: 'logout',
                imageType: 'icon',
                isActive: false,
                visible: true,
                color: "warn",
                onClick: () => this.auth.logout$.next()
              }, route)
            ];
            if (userMenu.some(x => x.isActive)) {
              this.userMenuExpanded$.next(true);
            } else {
              this.userMenuExpanded$.next(false);
            }
            return userMenu;
          }

          return [];

        })
      )
      ));

    this.menuItems$ = this.auth.user$.pipe(
      switchMap((user) => combineLatest([
        this.currentRoute$,
        this.es.events$,
        this.es.selectedEvent$.pipe(
          debug('Selected Event')
        )
      ]).pipe(
        map(([route, events, selectedEvent]) => {
          let menu: AppMenu;
          let eventsMenu = new MenuItem({
            label: 'menu.events',
            url: '/events',
            icon: 'event',
            imageType: 'icon',
            isActive: false,
            visible: true,
            badgeCounter: events.length,
            onClick: () => this.es.selectedEvent$.next(null)
          }, route);
          if (selectedEvent) {
            eventsMenu.children = [
              new MenuItem({
                label: selectedEvent.title,
                url: `/events/${selectedEvent.id}/overview`,
                imageType: 'icon',
                icon: 'home',
                isActive: false,
                visible: true
              }, route),
              new MenuItem({
                label: 'menu.settings',
                url: `/events/${selectedEvent.id}/settings`,
                imageType: 'icon',
                icon: 'settings',
                isActive: false,
                visible: true
              }, route),
              new MenuItem({
                label: 'menu.customFields',
                url: `/events/${selectedEvent.id}/form-fields`,
                imageType: 'icon',
                icon: 'contact_mail',
                isActive: false,
                visible: selectedEvent.enabled
              }, route),
              // new MenuItem({
              //   label: 'Liste',
              //   url: `/events/${selectedEvent.id}/lists`,
              //   imageType: 'icon',
              //   icon: 'format_list_bulleted',
              //   isActive: false,
              //   visible: true
              // }, route),
              new MenuItem({
                label: 'menu.groups',
                url: `/events/${selectedEvent.id}/clusters`,
                imageType: 'icon',
                icon: 'groups',
                isActive: false,
                visible: selectedEvent.enabled
              }, route),
              new MenuItem({
                label: 'menu.guests',
                url: `/events/${selectedEvent.id}/guests`,
                imageType: 'icon',
                icon: 'people',
                isActive: false,
                visible: selectedEvent.enabled
              }, route),
              new MenuItem({
                label: 'menu.email',
                url: `/events/${selectedEvent.id}/emails`,
                imageType: 'icon',
                icon: 'email',
                isActive: false,
                visible: selectedEvent.enabled && selectedEvent.features.email && !selectedEvent.finished
              }, route),
              new MenuItem({
                label: 'menu.rsvp',
                url: `/events/${selectedEvent.id}/rsvp`,
                imageType: 'icon',
                icon: 'mark_email_read',
                isActive: false,
                visible: selectedEvent.enabled && !selectedEvent.finished && selectedEvent.features.rsvp
              }, route),
              new MenuItem({
                label: 'menu.sms',
                url: `/events/${selectedEvent.id}/sms`,
                imageType: 'icon',
                icon: 'sms',
                isActive: false,
                visible: selectedEvent.enabled && selectedEvent.features.sms && !selectedEvent.finished
              }, route),
              new MenuItem({
                label: 'menu.website',
                url: `/events/${selectedEvent.id}/website`,
                imageType: 'icon',
                icon: 'web',
                isActive: false,
                visible: selectedEvent.enabled && selectedEvent.features.website
              }, route),
              // new MenuItem({
              //   label: 'menu.sendQr',
              //   url: `/events/${selectedEvent.id}/qr-codes`,
              //   imageType: 'icon',
              //   icon: 'send',
              //   isActive: false,
              //   visible: selectedEvent.enabled && !selectedEvent.finished
              // }, route),
              new MenuItem({
                label: 'menu.checkin',
                mode: 'button',
                url: `/events/${selectedEvent.id}/checkin`,
                imageType: 'icon',
                icon: 'qr_code_scanner',
                class: 'mat-bg-success !text-white',
                isActive: false,
                visible: selectedEvent.enabled && !selectedEvent.finished
              }, route),
              // Prominent CTA below the check-in button, same button styling but
              // primary. Pure action item (no url): opens the upgrade dialog. Live
              // events only (unpaid pays via the enable step, finished can't upgrade).
              new MenuItem({
                label: 'menu.upgrade',
                mode: 'button',
                imageType: 'icon',
                icon: 'rocket_launch',
                class: 'mat-bg-primary !text-white',
                isActive: false,
                visible: selectedEvent.enabled && !selectedEvent.finished,
                onClick: () => this.openUpgradeDialog()
              }, route)
            ];
          }


          if (user) {
            menu = {
              common: [
                // new MenuItem({
                //   label: 'Dashboard',
                //   url: '/dashboard',
                //   icon: 'dashboard',
                //   imageType: 'icon',
                //   isActive: false,
                //   visible: true
                // }, route),
                eventsMenu,
                // new MenuItem({
                //   label: 'Dev Test',
                //   url: '/dev-test',
                //   icon: 'bug_report',
                //   imageType: 'icon',
                //   isActive: false,
                //   visible: true
                // }, route),
                // new MenuItem({
                //   label: 'Utenti',
                //   url: '/users',
                //   icon: 'group',
                //   imageType: 'icon',
                //   isActive: false,
                //   visible: true
                // }, route)
              ],
              crm: [
                new MenuItem({
                  label: 'Gestione liste',
                  url: '/user-lists',
                  icon: 'format_list_bulleted',
                  imageType: 'icon',
                  isActive: false,
                  visible: true
                }, route),
                new MenuItem({
                  label: 'Comunicazioni',
                  url: '/communications',
                  icon: 'contact_mail',
                  imageType: 'icon',
                  isActive: false,
                  visible: true
                }, route)
              ],
              admin: [
                new MenuItem({
                  label: 'menu.usersAdmin',
                  url: '/users',
                  icon: 'people',
                  imageType: 'icon',
                  isActive: false,
                  visible: this.auth.role() === 'admin'
                }, route),
                new MenuItem({
                  // C8: distinct label so it doesn't read identically to the main
                  // "Tutti gli Eventi" (menu.events) — this one is the admin list.
                  label: 'menu.allEventsAdmin',
                  url: '/events-admin',
                  icon: 'event',
                  imageType: 'icon',
                  isActive: false,
                  visible: this.auth.role() === 'admin'
                }, route),
                new MenuItem({
                  label: 'menu.promoCode',
                  url: '/promo-code',
                  icon: 'local_activity',
                  imageType: 'icon',
                  isActive: false,
                  visible: this.auth.role() === 'admin'
                }, route),
                new MenuItem({
                  label: 'menu.formBuilder',
                  url: '/form-builder',
                  icon: 'dynamic_form',
                  imageType: 'icon',
                  isActive: false,
                  visible: this.auth.role() === 'admin'
                }, route),
                new MenuItem({
                  label: 'menu.emailEditor',
                  url: '/templates',
                  icon: 'email',
                  imageType: 'icon',
                  isActive: false,
                  visible: true
                }, route),
                // new MenuItem({
                //   label: 'Test',
                //   url: '/test',
                //   icon: 'debug',
                //   imageType: 'icon',
                //   isActive: false,
                //   visible: this.auth.role() === 'admin'
                // }, route),
                new MenuItem({
                  label: 'menu.log',
                  url: '/crm-log',
                  icon: 'storage',
                  imageType: 'icon',
                  isActive: false,
                  visible: this.auth.role() === 'admin'
                }, route)
              ]
            };
          } else {
            menu = {
              common: [],
              crm: [],
              admin: []
            };
          }

          // (Object.keys(menu) as (keyof typeof menu)[]).forEach(
          //   (category) => menu[category]?.forEach(item => {
          //     item.isActive = item.url ? route.startsWith(item.url) : false
          //   }));

          return menu;
        }),
      )
      ),
      // debug('Menu',  {trace: true})
      debug('Menu')
    );

    this.accountCreditsData$ = combineLatest([
      this.auth.user$.pipe(notNullOrUndefined()),
      this.es.refreshCredits$
    ]).pipe(
      switchMap(([user, _]) =>
        this.http.get<AccountCreditsData>(`${environment.appUrl}/account/credits`).pipe(
          this.hs.httpErrorHandler({ credits: 0, accountName: '' })
        )
      ),
      // Multicast — without this every `| async` / subscriber re-runs the cold
      // switchMap and fires its own GET /account/credits.
      shareReplay(1)
    );

  }

  getAccountCreditsInfo() {
    return this.http
  }

  /**
   * Open the event upgrade dialog from the sidebar. Dynamically imported so the
   * payment/PayPal component tree stays out of the eager core bundle. Mirrors the
   * upgrade-banner contract: a non-null close means the plan/limits changed, so
   * refresh the events list.
   */
  private async openUpgradeDialog(): Promise<void> {
    const {UpgradeEventDialogComponent} = await import(
      '../payment/upgrade-event-dialog/upgrade-event-dialog.component'
    );
    this.dialog.open(UpgradeEventDialogComponent).afterClosed().subscribe((res) => {
      if (res != null) {
        this.es.refreshEvents();
      }
    });
  }
}
